import { combineReducers } from 'redux';
import { combineActions, handleActions } from 'redux-actions';
import { defaultView } from 'utils/app-configs';
import moment from 'utils/cops-moment';
import { sortCOPSModelToParam } from 'utils/helpers';
import * as ganttActionTypes from '../../common/gantt/containers/gantt-constants';
import * as navigationActionTypes from '../../common/navigation/containers/constants';
import {
  AUTO_PLANNING_ACTION,
  AUTO_PLANNING_ACTION_SUCCESS,
  CREATE_AUTO_ORDERS,
  CREATE_AUTO_TICKETS,
  DELETE_ORDERS,
  DELETE_ORDERS_SUCCESS,
} from '../../common/toolbar/containers/constants';
import { worklistFilterIds } from '../../utils/app-configs';
import * as actionTypes from './constants';

export const definitions = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST,
      actionTypes.GET_WORKLIST_DEFINITIONS_FAILURE,
    )]: () => [],
    [actionTypes.GET_WORKLIST_DEFINITIONS_SUCCESS]: (state, { payload }) => payload,
  },
  [],
);

export const childColumnDetails = handleActions(
  {
    [actionTypes.SET_CHILD_DETAIL_PARAMS]: (action, { payload }) => payload,
    [actionTypes.CLEAR_WORKLIST_DATA]: () => [],
  },
  [],
);

export const isDefinitionsLoading = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_DEFINITIONS_FAILURE,
      actionTypes.GET_WORKLIST_DEFINITIONS_SUCCESS,
    )]: () => false,
    [actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST]: () => true,
  },
  false,
);

export const shouldRefetch = handleActions(
  {
    [combineActions(
      actionTypes.REFRESH_WORKGRID,
    )]: (state, { payload }) => payload,
    [CREATE_AUTO_TICKETS]: () => false,
  },
  false,
);

export const updatedRowsData = handleActions(
  {
    [combineActions(
      ganttActionTypes.ADD_BLOCK_DATA,
      ganttActionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA,
      ganttActionTypes.UPDATE_BLOCK_DATA,
      ganttActionTypes.DELETE_BLOCK,
      ganttActionTypes.RESTORE_BACKUP,
      ganttActionTypes.ADD_MULTI_BLOCK_DATA,
      ganttActionTypes.PUBLISH_ALL_TO_SAP,
      ganttActionTypes.PUBLISH_TO_SAP,
      ganttActionTypes.MARK_DATE_CONFIRMED,
      ganttActionTypes.MARK_DATE_UNCONFIRMED,
      ganttActionTypes.SPLIT_BLOCK,
      AUTO_PLANNING_ACTION,
      CREATE_AUTO_ORDERS,
      DELETE_ORDERS,
      actionTypes.REFRESH_WORKGRID,
      actionTypes.GET_WORKLIST_DATA_BY_ID_FAILURE,
      actionTypes.GET_WORKLIST_DATA_BY_ID_REQUEST,
    )]: () => [],
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_BY_ID_SUCCESS,
    )]: (state, { payload }) => payload.data,
  },
  [],
);

export const rowHighlightTrigger = handleActions(
  {
    [combineActions(
      actionTypes.TRIGGER_ROW_HIGHLIGHT,
    )]: (state, { payload }) => payload,
  },
  [],
);

export const mappedColumns = handleActions(
  {
    [combineActions(
      actionTypes.CLEAR_WORKLIST_DATA,
    )]: () => [],
    [combineActions(
      actionTypes.SET_MAPPED_COLUMNS,
    )]: (state, { payload }) => payload.data || [],
  },
  [],
);

export const refetchDataAfterVisualChange = handleActions(
  {
    [combineActions(
      actionTypes.CLEAR_WORKLIST_DATA,
    )]: () => false,
    [combineActions(
      actionTypes.SET_MAPPED_COLUMNS,
    )]: (state, { payload }) => payload.refetch,
  },
  false,
);

export const tableData = handleActions(
  {
    [combineActions(
      actionTypes.CLEAR_WORKLIST_DATA,
    )]: () => ({}),
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_REQUEST,
      actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST,
    )]: (state, { payload }) => {
      if (payload?.newWorklist) {
        return {
          data: state.data,
          totalDataCount: state.totalDataCount,
        };
      }
      return {};
    },
    [actionTypes.GET_WORKLIST_DATA_SUCCESS]: (state, { payload }) => ({
      data: payload.data,
      totalDataCount: payload.totalDataCount,
    }),
    [actionTypes.GET_WORKLIST_DATA_NEXT_SUCCESS]: (state, { payload }) => ({
      ...state,
      data: [...state.data || [], ...payload.data],
      totalDataCount: payload.totalDataCount,
    }),
    [DELETE_ORDERS_SUCCESS]: (state, { payload }) => ({
      ...state,
      data: state.data?.filter(d => !payload.actions?.find(a => a.id === d.id)),
      totalDataCount: state.totalDataCount - payload.actions.length,
    }),
  },
  {},
);

export const totalDataCount = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_FAILURE,
    )]: () => 0,
    [actionTypes.GET_WORKLIST_DATA_SUCCESS]: (state, { payload }) => {
      if (payload.shouldUpdateCount) {
        return payload.totalDataCount;
      }
      return state;
    },
  },
  0,
);

export const geoJson = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_REQUEST,
    )]: () => [],
    [actionTypes.GET_WORKLIST_DATA_SUCCESS]: (state, { payload }) => payload.geoJson || [],
    [actionTypes.GET_WORKLIST_DATA_NEXT_SUCCESS]: (state, { payload }) =>
      [...state || [], ...payload.geoJson || []],
  },
  [],
);

export const filterDefinitions = handleActions(
  {
    // also clear the table data when definition reloads
    [combineActions(actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST)]: () => [],
    [actionTypes.GET_WORKLIST_DATA_SUCCESS]: (state, { payload }) =>
      payload.filters?.map((filter) => {
        if (filter.id === worklistFilterIds.followedSince) {
          if (state?.find(s => s.id === worklistFilterIds.followedSince)) {
            return {
              ...filter,
              min: state?.find(s => s.id === worklistFilterIds.followedSince)?.min,
              max: moment(),
            };
          }
          return {
            ...filter,
            max: moment(),
          };
        }
        return filter;
      }),
  },
  [],
);

export const openActions = handleActions(
  {
    // also clear the table data when definition reloads
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.GET_WORKLIST_DATA_REQUEST,
    )]: () => '',
    [actionTypes.SET_OPEN_ACTIONS]: (state, { payload }) => payload,
  },
  '',
);

export const filterGroups = handleActions(
  {
    // also clear the table data when definition reloads
    [combineActions(actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST)]: () => [],
    [actionTypes.GET_WORKLIST_DATA_SUCCESS]: (state, { payload }) => payload.filterGroups || [],
  },
  [],
);

export const isDataLoading = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_SUCCESS,
      actionTypes.GET_WORKLIST_DATA_FAILURE,
    )]: () => false,
    // also set data loading when the definitions are loading
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_REQUEST,
      actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST,
    )]: () => true,
  },
  false,
);

export const isNextDataLoading = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_NEXT_SUCCESS,
      actionTypes.GET_WORKLIST_DATA_NEXT_FAILURE,
    )]: () => false,
    [actionTypes.GET_WORKLIST_DATA_NEXT_REQUEST]: () => true,
  },
  false,
);

export const activeFilters = handleActions(
  {
    [actionTypes.CLEAR_WORKLIST_DATA]: () => ({}),
    [actionTypes.UPDATE_FILTERS]: (state, { payload }) => payload,
  },
  {},
);

export const activeSort = handleActions(
  {
    [actionTypes.CLEAR_WORKLIST_DATA]: () => '',
    [actionTypes.GET_WORKLIST_METADATA_SUCCESS]: (state, { payload }) =>
      sortCOPSModelToParam(payload.defaultSorting),
    [actionTypes.UPDATE_WORKLIST_SORT]: (state, { payload }) => payload,
  },
  '',
);

export const activeView = handleActions(
  {
    [actionTypes.CLEAR_WORKLIST_DATA]: () => null,
    [actionTypes.UPDATE_VIEW]: (state, { payload }) => payload,
  },
  null,
);

export const next = handleActions(
  {
    // also clear the next prop when definition reloads
    [combineActions(actionTypes.GET_WORKLIST_DEFINITIONS_REQUEST)]: () => ({}),
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_SUCCESS,
      actionTypes.GET_WORKLIST_DATA_NEXT_SUCCESS,
    )]: (state, { payload }) => payload.next,
  },
  [],
);

export const views = handleActions(
  {
    [combineActions(
      actionTypes.CLEAR_VIEWS,
      actionTypes.GET_WORKLIST_VIEWS_REQUEST,
      actionTypes.GET_WORKLIST_VIEWS_FAILURE,
    )]: () => null,
    [combineActions(
      actionTypes.GET_WORKLIST_VIEWS_SUCCESS,
      actionTypes.UPDATE_WORKLIST_VIEWS_SUCCESS,
      actionTypes.CREATE_WORKLIST_VIEW_SUCCESS,
    )]: (state, { payload }) => [...payload, defaultView],
  },
  null,
);

export const defaultExpanded = handleActions(
  {
    [combineActions(
      actionTypes.CLEAR_VIEWS,
      actionTypes.GET_WORKLIST_VIEWS_REQUEST,
    )]: () => false,
    [actionTypes.UPDATE_DEFAULT_EXPAND]: (state, { payload }) => payload || false,
  },
  false,
);

export const currentWorklistId = handleActions(
  {
    [actionTypes.SET_CURRENT_WORKLIST_ID]: (state, { payload }) => payload || null,
  },
  null,
);

export const isLoadingViews = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_VIEWS_SUCCESS,
      actionTypes.GET_WORKLIST_VIEWS_FAILURE,
    )]: () => false,
    [actionTypes.GET_WORKLIST_VIEWS_REQUEST]: () => true,
  },
  false,
);

export const isSavingViews = handleActions(
  {
    [combineActions(
      actionTypes.CREATE_WORKLIST_VIEW_SUCCESS,
      actionTypes.CREATE_WORKLIST_VIEW_FAILURE,
      actionTypes.UPDATE_WORKLIST_VIEWS_SUCCESS,
      actionTypes.UPDATE_WORKLIST_VIEWS_FAILURE,
    )]: () => false,
    [combineActions(
      actionTypes.CREATE_WORKLIST_VIEW_REQUEST,
      actionTypes.UPDATE_WORKLIST_VIEWS_REQUEST,
    )]: () => true,
  },
  false,
);

export const saveViewsSuccess = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.CREATE_WORKLIST_VIEW_REQUEST,
      actionTypes.CREATE_WORKLIST_VIEW_FAILURE,
      actionTypes.UPDATE_WORKLIST_VIEWS_REQUEST,
      actionTypes.UPDATE_WORKLIST_VIEWS_FAILURE,
      navigationActionTypes.RESET_NOTIFICATION,
    )]: () => false,
    [combineActions(
      actionTypes.CREATE_WORKLIST_VIEW_SUCCESS,
      actionTypes.UPDATE_WORKLIST_VIEWS_SUCCESS,
    )]: () => true,
  },
  false,
);

export const error = handleActions(
  {
    [combineActions(
      actionTypes.CREATE_WORKLIST_VIEW_REQUEST,
      actionTypes.CREATE_WORKLIST_VIEW_SUCCESS,
      actionTypes.UPDATE_WORKLIST_VIEWS_REQUEST,
      actionTypes.UPDATE_WORKLIST_VIEWS_SUCCESS,
    )]: () => ({}),
    [combineActions(
      actionTypes.CREATE_WORKLIST_VIEW_FAILURE,
      actionTypes.UPDATE_WORKLIST_VIEWS_FAILURE,
    )]: (state, { payload }) => payload.error,
  },
  {},
);

export const fetchError = handleActions(
  {
    [combineActions(
      actionTypes.RESET_ERROR,
      actionTypes.UPDATE_FILTERS,
      actionTypes.GET_WORKLIST_DATA_SUCCESS,
      actionTypes.GET_WORKLIST_DATA_REQUEST,
      actionTypes.GET_WORKLIST_DATA_NEXT_SUCCESS,
      actionTypes.GET_WORKLIST_DATA_NEXT_REQUEST,
      actionTypes.GET_WORKLIST_METADATA_REQUEST,
      actionTypes.GET_WORKLIST_METADATA_SUCCESS,
    )]: () => ({}),
    [combineActions(
      actionTypes.GET_WORKLIST_DATA_FAILURE,
      actionTypes.GET_WORKLIST_DATA_NEXT_FAILURE,
      actionTypes.GET_WORKLIST_METADATA_FAILURE,
    )]: (state, { payload }) => payload.error,
  },
  {},
);

export const childSelection = handleActions(
  {
    [actionTypes.SET_CHILD_SELECTION]: (state = [], { payload }) => {
      const { nodeData, isSelected } = payload;
      // Check if the nodeData object already exists in the state
      const index = state?.findIndex(item => item?.id === nodeData?.id);
      // Add the nodeData object to the state if it doesn't already exist
      if (isSelected && index === -1) {
        return [...state, nodeData];
      }
      // Delete if deselected and if in state
      if (!isSelected && !index !== -1) {
        const filteredState = state?.filter(item => item?.id !== nodeData?.id);
        return filteredState;
      }
      return state;
    },
    [ganttActionTypes.DELETE_BLOCK_SUCCESS]: (state = [], { payload }) =>
      state?.filter(s => s.id !== payload.id),
    [combineActions(
      AUTO_PLANNING_ACTION_SUCCESS,
      ganttActionTypes.UPDATE_MULTI_PLANNING_BLOCK_DATA_SUCCESS,
      actionTypes.CLEAR_WORKLIST_DATA,
      ganttActionTypes.ADD_MULTI_BLOCK_DATA_SUCCESS,
      ganttActionTypes.UPDATE_BLOCK_DATA_SUCCESS,
    )]: () => [],
  },
  [],
);

export const openFilterId = handleActions(
  {
    [actionTypes.SET_OPEN_FILTER_ID]: (state, { payload }) => payload,
  },
  '',
);

export const metaData = handleActions(
  {
    [combineActions(
      actionTypes.CLEAR_WORKLIST_DATA,
      actionTypes.GET_WORKLIST_METADATA_REQUEST,
    )]: () => ({}),
    [actionTypes.GET_WORKLIST_METADATA_SUCCESS]: (state, { payload }) => ({
      ...payload,
      defaultSorting: sortCOPSModelToParam(payload.defaultSorting),
    }),
  },
  {},
);

export const isLoadingMetaData = handleActions(
  {
    [combineActions(
      actionTypes.GET_WORKLIST_METADATA_SUCCESS,
      actionTypes.GET_WORKLIST_METADATA_FAILURE,
    )]: () => false,
    [actionTypes.GET_WORKLIST_METADATA_REQUEST]: () => true,
  },
  false,
);

export const shouldResetAllAdditionalFilterParams = handleActions(
  {
    [actionTypes.RESET_ALL_ADDITIONAL_FILTER_PARAMS]: (state, { payload }) => payload,
  },
  false,
);


export default combineReducers({
  isDefinitionsLoading,
  isDataLoading,
  isNextDataLoading,
  definitions,
  tableData,
  filterDefinitions,
  activeFilters,
  activeSort,
  activeView,
  next,
  views,
  defaultExpanded,
  isLoadingViews,
  isSavingViews,
  saveViewsSuccess,
  error,
  childSelection,
  geoJson,
  totalDataCount,
  filterGroups,
  shouldRefetch,
  openActions,
  currentWorklistId,
  childColumnDetails,
  fetchError,
  mappedColumns,
  refetchDataAfterVisualChange,
  openFilterId,
  metaData,
  isLoadingMetaData,
  updatedRowsData,
  rowHighlightTrigger,
  shouldResetAllAdditionalFilterParams,
});
